import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Button,
  Snackbar,
  Alert,
  Select,
} from "@mui/material";
import { useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  setDoc,
  doc as docRef,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import AdminProfileDialog from "../components/AdminProfileDialog";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Build as BuildIcon,
  Category as CategoryIcon,
  BookOnline as BookOnlineIcon,
  HowToReg as HowToRegIcon,
  LocalOffer as LocalOfferIcon,
  Settings as SettingsIcon,
  Policy as PolicyIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
  Assignment as AssignmentIcon,
  MonetizationOn as MonetizationOnIcon,
  Group as GroupIcon,
  Campaign as CampaignIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
} from "@mui/icons-material";
import logoImg from "../assets/images/MLFLogo.jpg";
import {
  DASHBOARD_PERMISSIONS,
  DASHBOARD_ROUTE_PERMISSIONS,
} from "../constants/dashboardAccess";
import {
  DASHBOARD_COUNTRIES,
  FORCED_COUNTRY,
  useDashboardCountry,
} from "../context/DashboardCountryContext";
import { indiaOperations } from "../utils/indiaOperationsSource";

const drawerWidth = 240;
const isIndiaPath = (pathname) => pathname.startsWith("/dashboard/india");
// India pages reuse the Australian titles: /dashboard/india/clients -> /dashboard/clients,
// /dashboard/india -> /dashboard.
const toAuPath = (pathname) =>
  pathname.replace(/^\/dashboard\/india(?=\/|$)/, "/dashboard");

const Dashboard = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportCaseSnackbar, setSupportCaseSnackbar] = useState({
    open: false,
    message: "",
    targetId: null,
  });
  const { currentUser, logout, hasPermission, getFirstAllowedDashboardPath } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { country, setCountry } = useDashboardCountry();
  // India pages built so far; the rest of the menu follows as India features land.
  const indiaOverviewItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard/india",
      permission: DASHBOARD_PERMISSIONS.OVERVIEW,
    },
  ].filter((item) => hasPermission(item.permission));
  const indiaUserItems = [
    {
      text: "Customers",
      icon: <PeopleIcon />,
      path: "/dashboard/india/customers",
      permission: DASHBOARD_PERMISSIONS.CUSTOMERS,
    },
    {
      text: "Provider",
      icon: <BusinessIcon />,
      path: "/dashboard/india/clients",
      permission: DASHBOARD_PERMISSIONS.PROVIDERS,
    },
    {
      text: "Request",
      icon: <HowToRegIcon />,
      path: "/dashboard/india/client-approvals",
      permission: DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION,
    },
  ].filter((item) => hasPermission(item.permission));
  const indiaServiceItems = [
    {
      text: "Services",
      icon: <BuildIcon />,
      path: "/dashboard/india/services",
      permission: DASHBOARD_PERMISSIONS.SERVICES,
    },
    {
      text: "Categories",
      icon: <CategoryIcon />,
      path: "/dashboard/india/categories",
      permission: DASHBOARD_PERMISSIONS.CATEGORIES,
    },
    {
      text: "Bookings",
      icon: <BookOnlineIcon />,
      path: "/dashboard/india/bookings",
      permission: DASHBOARD_PERMISSIONS.BOOKINGS,
    },
    {
      text: "Lead Pool",
      icon: <AssignmentIcon />,
      path: "/dashboard/india/reassign-leads",
      permission: DASHBOARD_PERMISSIONS.OPEN_POOL,
    },
  ].filter((item) => hasPermission(item.permission));
  const indiaSupportItems = [
    {
      text: "Support",
      icon: <BuildIcon />,
      path: "/dashboard/india/support",
      permission: DASHBOARD_PERMISSIONS.HELPDESK,
    },
  ].filter((item) => hasPermission(item.permission));
  const indiaSettingsItems = [
    {
      text: "Payout Settings",
      icon: <SettingsIcon />,
      path: "/dashboard/india/settings",
      permission: DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS,
    },
  ].filter((item) => hasPermission(item.permission));
  const indiaMenuItems = [
    ...indiaOverviewItems,
    ...indiaUserItems,
    ...indiaServiceItems,
    ...indiaSupportItems,
    ...indiaSettingsItems,
  ];
  const canUseIndia = indiaMenuItems.length > 0;
  // The India preview build never falls back to the Australian pages.
  const activeCountry = FORCED_COUNTRY || (country === "IN" && canUseIndia ? "IN" : "AU");
  // Same start page as Australia: the Dashboard when allowed.
  const indiaHome = indiaMenuItems[0]?.path;
  const pagePath = toAuPath(location.pathname);

  // Keep the open page in the selected country's set of pages.
  useEffect(() => {
    const onIndiaPage = isIndiaPath(location.pathname);
    if (activeCountry === "IN" && !onIndiaPage) {
      if (indiaHome) navigate(indiaHome, { replace: true });
    } else if (activeCountry === "AU" && onIndiaPage) {
      navigate("/dashboard", { replace: true });
    }
  }, [activeCountry, indiaHome, location.pathname, navigate]);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
    handleClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;

  const handleLogoClick = () => {
    navigate("/dashboard");
    // close mobile drawer if open
    setMobileOpen(false);
  };

  // Real-time listeners for new bookings, customers, providers and services
  useEffect(() => {
    // Persist notifications into a 'notifications' collection so they can be managed/read/deleted and survive refresh.
    const unsubscribes = [];
    try {
      // We must ignore the initial snapshot for each collection: onSnapshot's first callback
      // reports existing documents as 'added'. If we write notifications for those, they
      // will be recreated every time the dashboard mounts (e.g., after logout/login).
      // Use flags to skip the very first snapshot invocation for each listener.
      let initialBookings = true;
      let initialUsers = true;
      let initialServices = true;
      let initialSupportCases = true;
      // helper to write notification doc with deterministic id to avoid duplicates
      const writeNotification = async (id, payload) => {
        try {
          const docId = `${payload.type}_${id}`;
          const ref = docRef(db, "notifications", docId);
          // If the notification already exists, update mutable fields but do NOT reset `read` or `createdAt`.
          const existing = await getDoc(ref);
          if (existing && existing.exists()) {
            await updateDoc(ref, {
              sourceId: id,
              type: payload.type,
              message: payload.message,
              targetRoute: payload.targetRoute || null,
              targetId: payload.targetId || null,
              imageURL: payload.imageURL || null,
            });
          } else {
            await setDoc(
              ref,
              {
                id: docId,
                sourceId: id,
                type: payload.type,
                message: payload.message,
                targetRoute: payload.targetRoute || null,
                targetId: payload.targetId || null,
                imageURL: payload.imageURL || null,
                read: false,
                createdAt: serverTimestamp(),
                expiresAt: Timestamp.fromMillis(
                  Date.now() + 7 * 24 * 60 * 60 * 1000
                ), // 7 days
              },
              { merge: true }
            );
          }
        } catch (err) {
          console.error("writeNotification failed", err);
        }
      };

      // bookings
      const bq = query(
        collection(db, "bookings"),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      const ub = onSnapshot(bq, (snap) => {
        // skip initial snapshot which contains existing docs
        if (initialBookings) {
          initialBookings = false;
          return;
        }
        snap.docChanges().forEach((change) => {
          if (change.type === "added") {
            const data = change.doc.data();
            writeNotification(change.doc.id, {
              type: "booking",
              message: `New booking: ${
                data.serviceName || data.serviceTitle || change.doc.id
              }`,
              targetRoute: "/dashboard/bookings",
              targetId: change.doc.id,
              imageURL: data.customerPhotoURL || data.photoURL || null,
            });
          }
        });
      });
      unsubscribes.push(ub);

      // users (customers/providers)
      const uq = query(
        collection(db, "users"),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      const uu = onSnapshot(uq, (snap) => {
        if (initialUsers) {
          initialUsers = false;
          return;
        }
        snap.docChanges().forEach((change) => {
          if (change.type === "added") {
            const data = change.doc.data();
            const roles = data.roles || {};
            if (
              roles.client ||
              roles.provider ||
              data.role === "client" ||
              data.role === "provider"
            ) {
              writeNotification(change.doc.id, {
                type: "provider",
                message: `New provider joined: ${data.name || data.email}`,
                targetRoute: "/dashboard/clients",
                targetId: change.doc.id,
                imageURL: data.photoURL || data.imageURL || null,
              });
            } else if (roles.customer || data.role === "customer") {
              writeNotification(change.doc.id, {
                type: "customer",
                message: `New customer joined: ${data.name || data.email}`,
                targetRoute: "/dashboard/customers",
                targetId: change.doc.id,
                imageURL: data.photoURL || data.imageURL || null,
              });
            }
          }
        });
      });
      unsubscribes.push(uu);

      // services
      const sq = query(
        collection(db, "services"),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      const us = onSnapshot(sq, (snap) => {
        if (initialServices) {
          initialServices = false;
          return;
        }
        snap.docChanges().forEach((change) => {
          if (change.type === "added") {
            const data = change.doc.data();
            writeNotification(change.doc.id, {
              type: "service",
              message: `New service added: ${data.title || data.name}`,
              targetRoute: "/dashboard/services",
              targetId: change.doc.id,
              imageURL: data.imageURL || null,
            });
          }
        });
      });
      unsubscribes.push(us);

      // support cases
      const supportQuery = query(
        collection(db, "supportCases"),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      const usc = onSnapshot(supportQuery, (snap) => {
        if (initialSupportCases) {
          initialSupportCases = false;
          return;
        }

        snap.docChanges().forEach((change) => {
          if (change.type === "added") {
            const data = change.doc.data() || {};
            const caseCode =
              data.caseId ||
              `CASE-${String(change.doc.id || "").slice(-8).toUpperCase()}`;
            const subject = data.subject || "Support request";
            const requester = data.requesterName || data.requesterEmail || "User";

            writeNotification(change.doc.id, {
              type: "support",
              message: `New support case: ${caseCode} - ${subject}`,
              targetRoute: "/dashboard/support",
              targetId: change.doc.id,
              imageURL: null,
            });

            setSupportCaseSnackbar({
              open: true,
              message: `${caseCode} created by ${requester}`,
              targetId: change.doc.id,
            });
          }
        });
      });
      unsubscribes.push(usc);
    } catch (err) {
      console.error("Notification listener error", err);
    }

    return () => unsubscribes.forEach((u) => typeof u === "function" && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // India admin notices live in the India database; read them through callables.
  const [indiaNotifications, setIndiaNotifications] = useState([]);
  useEffect(() => {
    if (activeCountry !== "IN") return undefined;
    let active = true;
    const load = () =>
      indiaOperations
        .listNotifications()
        .then((items) => active && setIndiaNotifications(items))
        .catch((err) => console.error("India notifications failed", err));
    load();
    const timer = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [activeCountry]);

  const isIndiaBell = activeCountry === "IN";
  const shownNotifications = isIndiaBell ? indiaNotifications : notifications;
  const updateIndiaNotifications = async (ids, action) => {
    await indiaOperations.updateNotifications(ids, action);
    setIndiaNotifications((prev) =>
      action === "delete"
        ? prev.filter((n) => !ids.includes(n.id))
        : prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n))
    );
  };

  const notificationsCount = shownNotifications.filter((n) => !n.read).length;

  const handleNotifOpen = (e) => {
    setNotifAnchorEl(e.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const closeSupportCaseSnackbar = () => {
    setSupportCaseSnackbar({ open: false, message: "", targetId: null });
  };

  const openSupportCaseFromSnackbar = () => {
    const route = "/dashboard/support";
    const requiredPermission = DASHBOARD_ROUTE_PERMISSIONS[route];

    if (requiredPermission && !hasPermission(requiredPermission)) {
      const fallbackPath = getFirstAllowedDashboardPath();
      if (fallbackPath && fallbackPath !== "/login") {
        navigate(fallbackPath);
      }
      closeSupportCaseSnackbar();
      return;
    }

    navigate(route, {
      state: supportCaseSnackbar.targetId
        ? { id: supportCaseSnackbar.targetId }
        : undefined,
    });
    closeSupportCaseSnackbar();
  };

  // Listen for notifications. We query by createdAt (desc) and filter expired items client-side
  useEffect(() => {
    try {
      const nq = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc")
      );
      const unsub = onSnapshot(
        nq,
        (snap) => {
          const nowMs = Date.now();
          const allowedPrefixRegex =
            /^(?:booking|new|provider|service|customer|support)_[A-Za-z0-9_-]{3,}$/i;
          const titleBlacklist = new Set([
            "Booking Accepted!",
            "Service Completed!",
            "New Booking Request!",
          ]);

          const items = snap.docs
            .map((d) => {
              const data = d.data() || {};
              // Normalize message: prefer `message`, fallback to `body` or `title`.
              const message = data.message || data.body || data.title || "";
              return { id: d.id, ...data, message };
            })
            .filter((item) => {
              // Only accept deterministic notification ids created by writeNotification
              // Expected form: <type>_<id> e.g. booking_Ui6LY0A3XqHN5vurNUr9, new_653572, provider_723687
              if (!item.id || !allowedPrefixRegex.test(item.id)) return false;

              // If message or title is blacklisted, skip it (these came from other notification writers)
              if (
                (item.title && titleBlacklist.has(item.title)) ||
                (item.message && titleBlacklist.has(item.message))
              ) {
                return false;
              }

              // drop notifications without a meaningful message
              if (
                !item.message ||
                (typeof item.message === "string" && item.message.trim() === "")
              )
                return false;
              // ensure message contains at least one alphanumeric character (not just punctuation)
              if (
                typeof item.message === "string" &&
                !/[A-Za-z0-9]/.test(item.message)
              )
                return false;

              if (!item.expiresAt) return true;
              // expiresAt may be a Firestore Timestamp or millis
              const exp =
                item.expiresAt && item.expiresAt.toMillis
                  ? item.expiresAt.toMillis()
                  : typeof item.expiresAt === "number"
                  ? item.expiresAt
                  : null;
              return exp ? exp >= nowMs : true;
            });
          setNotifications(items);
        },
        (err) => console.error("notifications listener error", err)
      );

      return () => unsub();
    } catch (err) {
      console.error("setup notifications listener failed", err);
    }
  }, []);

  const handleNotificationClick = async (n) => {
    try {
      // Mark notification as read
      if (n && n.id && isIndiaBell) {
        await updateIndiaNotifications([n.id], "read");
      } else if (n && n.id) {
        await updateDoc(docRef(db, "notifications", n.id), { read: true });
      }
    } catch (err) {
      console.error("mark read failed", err);
    }

    // Navigate to the target route (pass id in state if available)
    try {
      if (n?.targetRoute) {
        const requiredPermission = DASHBOARD_ROUTE_PERMISSIONS[toAuPath(n.targetRoute)];
        if (requiredPermission && !hasPermission(requiredPermission)) {
          const fallbackPath = getFirstAllowedDashboardPath();
          if (fallbackPath && fallbackPath !== "/login") {
            navigate(fallbackPath);
          }
          handleNotifClose();
          return;
        }
        navigate(n.targetRoute, { state: { id: n.targetId } });
      }
    } catch (err) {
      console.error("navigate on notification failed", err);
    }
    handleNotifClose();
  };

  const handleDeleteNotification = async (id) => {
    try {
      if (isIndiaBell) {
        await updateIndiaNotifications([id], "delete");
        return;
      }
      await deleteDoc(docRef(db, "notifications", id));
    } catch (err) {
      console.error("delete notification failed", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unread = shownNotifications.filter((n) => !n.read);
      if (isIndiaBell) {
        if (unread.length) await updateIndiaNotifications(unread.map((n) => n.id), "read");
        return;
      }
      await Promise.all(
        unread.map((n) =>
          updateDoc(docRef(db, "notifications", n.id), { read: true })
        )
      );
    } catch (err) {
      console.error("mark all read failed", err);
    }
  };

  const menuSections = [
    {
      title: "Overview",
      items: [
        {
          text: "Dashboard",
          icon: <DashboardIcon />,
          path: "/dashboard",
          permission: DASHBOARD_PERMISSIONS.OVERVIEW,
        },
      ],
    },
    {
      title: "Users",
      items: [
        {
          text: "Customers",
          icon: <PeopleIcon />,
          path: "/dashboard/customers",
          permission: DASHBOARD_PERMISSIONS.CUSTOMERS,
        },
        // Display label changed per request (keeps same route)
        {
          text: "Provider",
          icon: <BusinessIcon />,
          path: "/dashboard/clients",
          permission: DASHBOARD_PERMISSIONS.PROVIDERS,
        },
        // Display label changed per request (keeps same route)
        {
          text: "Request",
          icon: <HowToRegIcon />,
          path: "/dashboard/client-approvals",
          permission: DASHBOARD_PERMISSIONS.PROVIDER_VERIFICATION,
        },
      ],
    },
    {
      title: "Services & Bookings",
      items: [
        {
          text: "Services",
          icon: <BuildIcon />,
          path: "/dashboard/services",
          permission: DASHBOARD_PERMISSIONS.SERVICES,
        },
        {
          text: "Service Requests",
          icon: <PlaylistAddCheckIcon />,
          path: "/dashboard/service-requests",
          permission: DASHBOARD_PERMISSIONS.SERVICES,
        },
        {
          text: "Categories",
          icon: <CategoryIcon />,
          path: "/dashboard/categories",
          permission: DASHBOARD_PERMISSIONS.CATEGORIES,
        },
        {
          text: "Category Requests",
          icon: <PlaylistAddCheckIcon />,
          path: "/dashboard/category-requests",
          permission: DASHBOARD_PERMISSIONS.CATEGORIES,
        },
        {
          text: "Coupons",
          icon: <LocalOfferIcon />,
          path: "/dashboard/coupons",
          permission: DASHBOARD_PERMISSIONS.COUPONS,
        },
        {
          text: "Bookings",
          icon: <BookOnlineIcon />,
          path: "/dashboard/bookings",
          permission: DASHBOARD_PERMISSIONS.BOOKINGS,
        },
        {
          text: "Payment Lifecycle",
          icon: <MonetizationOnIcon />,
          path: "/dashboard/payment-lifecycle",
          permission: DASHBOARD_PERMISSIONS.BOOKINGS,
        },
        {
          text: "Open Pool",
          icon: <AssignmentIcon />,
          path: "/dashboard/reassign-bookings",
          permission: DASHBOARD_PERMISSIONS.OPEN_POOL,
        },
        {
          text: "Lead Pool",
          icon: <AssignmentIcon />,
          path: "/dashboard/reassign-leads",
          permission: DASHBOARD_PERMISSIONS.OPEN_POOL,
        },
        {
          text: "Refunds",
          icon: <MonetizationOnIcon />,
          path: "/dashboard/refunds",
          permission: DASHBOARD_PERMISSIONS.REFUNDS,
        },
      ],
    },
    {
      title: "Support",
      items: [
        {
          text: "Support",
          icon: <BuildIcon />,
          path: "/dashboard/support",
          permission: DASHBOARD_PERMISSIONS.HELPDESK,
        },
      ],
    },
    {
      title: "Settings",
      items: [
        // Display label changed to "Payout Settings"
        {
          text: "Payout Settings",
          icon: <SettingsIcon />,
          path: "/dashboard/settings",
          permission: DASHBOARD_PERMISSIONS.PAYOUT_SETTINGS,
        },
        {
          text: "Marketing",
          icon: <CampaignIcon />,
          path: "/dashboard/marketing",
          permission: DASHBOARD_PERMISSIONS.MARKETING,
        },
        {
          text: "Email Templates",
          icon: <NotificationsIcon />,
          path: "/dashboard/communication",
          permission: DASHBOARD_PERMISSIONS.EMAIL_TEMPLATES,
        },
        {
          text: "Notification Templates",
          icon: <NotificationsIcon />,
          path: "/dashboard/notifications",
          permission: DASHBOARD_PERMISSIONS.SMS_TEMPLATES,
        },
        {
          text: "Policy & Terms",
          icon: <PolicyIcon />,
          path: "/dashboard/policies",
          permission: DASHBOARD_PERMISSIONS.POLICIES,
        },
        {
          text: "Team Access",
          icon: <GroupIcon />,
          path: "/dashboard/team-access",
          permission: DASHBOARD_PERMISSIONS.TEAM_ACCESS,
        },
      ],
    },
  ];

  const indiaMenuSections = [
    { title: "Overview", items: indiaOverviewItems },
    { title: "Users", items: indiaUserItems },
    { title: "Services & Bookings", items: indiaServiceItems },
    { title: "Support", items: indiaSupportItems },
    { title: "Settings", items: indiaSettingsItems },
  ];

  const visibleMenuSections = (activeCountry === "IN" ? indiaMenuSections : menuSections)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasPermission(item.permission)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <Box
      sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      {/* Left Sidebar */}
      <Drawer
        variant={isMdUp ? "permanent" : "temporary"}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
            backgroundColor: "white",
          },
        }}
      >
        {/* Logo/Brand */}
        <Box
          role="button"
          tabIndex={0}
          onClick={handleLogoClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleLogoClick();
          }}
          sx={{
            p: 1,
            m: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            border: "2px solid #4A5BF4",
            borderRadius: 3,
            backgroundColor: "white",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {logoImg ? (
              <Box
                component="img"
                src={logoImg}
                alt="MyLocalForce"
                sx={{ width: 36, height: 36, objectFit: "contain" }}
              />
            ) : (
              <Box
                sx={{
                  backgroundColor: "#4A5BF4",
                  color: "white",
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HomeIcon sx={{ fontSize: 22 }} />
              </Box>
            )}
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#4A5BF4",
              fontSize: "1.05rem",
              letterSpacing: "0.3px",
            }}
          >
            MyLocalForce
          </Typography>
        </Box>

        {/* User Info */}
        {/* <Box sx={{ px: 1, pt: 1, pb: 1 }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Edit admin profile"
            onClick={() => {
              setProfileOpen(true);
              setMobileOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setProfileOpen(true);
                setMobileOpen(false);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1,
              backgroundColor: "#F5F7FF",
              borderRadius: 2,
              cursor: "pointer",
            }}
          >
            <Avatar
              src={currentUser?.photoURL || undefined}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: "#4A5BF4",
                fontWeight: 600,
                fontSize: "1.1rem",
              }}
            >
              {!currentUser?.photoURL &&
                (currentUser?.displayName?.charAt(0) ||
                  currentUser?.email?.charAt(0)?.toUpperCase() ||
                  "A")}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "0.95rem" }}
              >
                {currentUser?.displayName || "Admin User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#666", fontSize: "0.75rem" }}
              >
                {currentUser?.email || "admin@example.com"}
              </Typography>
            </Box>
          </Box>
        </Box> */}

        {/* Main Menu (grouped) */}
        <Box>
          {visibleMenuSections.map((section) => (
            <Box key={section.title} sx={{ px: 2, mt: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#9CA3AF",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  letterSpacing: "0.5px",
                  mb: 1,
                }}
              >
                {section.title}
              </Typography>
              <hr style={{ border: "1px solid #e4e4e7" }} />
              <List>
                {section.items.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                    {/**
                     * Styling intent:
                     * - show a blue left bar when selected
                     * - icon inside a rounded light background when selected
                     * - right chevron on the active item
                     * Keep routes unchanged; only presentation changes.
                     */}
                    <ListItemButton
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      selected={isActive(item.path)}
                      sx={{
                        borderRadius: 2,
                        py: 0.5,
                        px: 1,
                        position: "relative",
                        borderLeft: isActive(item.path)
                          ? "4px solid #4A5BF4"
                          : "4px solid transparent",
                        "&.Mui-selected": {
                          backgroundColor: "#F3F7FF",
                          color: "#0f172a",
                          "&:hover": {
                            backgroundColor: "#E9F0FF",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#FBFCFE",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, mr: 1 }}>
                        <Box
                          sx={{
                            width: 25,
                            height: 25,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: isActive(item.path)
                              ? "#E8EDFF"
                              : "transparent",
                            color: isActive(item.path) ? "#4A5BF4" : "#6B7280",
                          }}
                        >
                          {item.icon}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: "0.95rem",
                          fontWeight: 400,
                          color: isActive(item.path) ? "#0f172a" : "#374151",
                        }}
                      />
                      {isActive(item.path) && (
                        <ChevronRightIcon sx={{ color: "#4A5BF4" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
            {/* Menu button for small screens */}
            {!isMdUp && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon sx={{ color: "#666" }} />
              </IconButton>
            )}
            {/* Page Title */}
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 500, color: "#1a1a1a", mb: 0.5 }}
              >
                {pagePath === "/dashboard" && "Dashboard"}
                {pagePath === "/dashboard/customers" && "Customers"}
                {pagePath === "/dashboard/clients" &&
                  "Service Providers"}
                {pagePath === "/dashboard/client-approvals" &&
                  "New Provider Requests"}
                {pagePath === "/dashboard/services" && "Services"}
                {pagePath === "/dashboard/service-requests" &&
                  "Service Requests"}
                {pagePath === "/dashboard/coupons" && "Coupons"}
                {pagePath === "/dashboard/bookings" && "Bookings"}
                {pagePath === "/dashboard/payment-lifecycle" &&
                  "Payment Lifecycle"}
                {pagePath === "/dashboard/reassign-bookings" && "Open Pool"}
                {pagePath === "/dashboard/reassign-leads" && "Lead Pool"}
                {pagePath === "/dashboard/refunds" && "Refunds"}
                {pagePath === "/dashboard/categories" && "Categories"}
                {pagePath === "/dashboard/category-requests" &&
                  "Category Requests"}
                {pagePath === "/dashboard/support" && "Support"}
                {pagePath === "/dashboard/settings" &&
                  "Payment Settings"}
                {pagePath === "/dashboard/marketing" && "Marketing"}
                {pagePath === "/dashboard/communication" &&
                  "Email Templates"}
                {(pagePath === "/dashboard/notifications" ||
                  pagePath === "/dashboard/sms") &&
                  "Notification Templates"}
                {pagePath === "/dashboard/team-access" &&
                  "Team Access"}
                {(pagePath === "/dashboard/policies" ||
                  pagePath === "/dashboard/privacy-policy" ||
                  pagePath === "/dashboard/terms-provider" ||
                  pagePath === "/dashboard/terms-customer") &&
                  "Policy & Terms"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: "0.875rem" }}
              >
                {pagePath === "/dashboard" &&
                  "Overview of your platform statistics"}
                {pagePath === "/dashboard/customers" &&
                  "Manage customer accounts"}
                {pagePath === "/dashboard/clients" &&
                  "Manage service provider accounts"}
                {pagePath === "/dashboard/client-approvals" &&
                  "Review and approve new providers"}
                {pagePath === "/dashboard/services" &&
                  "Manage available services"}
                {pagePath === "/dashboard/service-requests" &&
                  "Review provider requests for services not yet listed"}
                {pagePath === "/dashboard/coupons" &&
                  "Create and manage promotion codes"}
                {pagePath === "/dashboard/bookings" &&
                  "View and manage all bookings"}
                {pagePath === "/dashboard/payment-lifecycle" &&
                  "Track payment lifecycle from order creation to payout and profit booking"}
                {pagePath === "/dashboard/reassign-bookings" &&
                  "Assign providers to pending and rejected bookings"}
                {pagePath === "/dashboard/reassign-leads" &&
                  "Arrange providers for open leads and assign manually"}
                {pagePath === "/dashboard/refunds" &&
                  "Process refunds and manage refund requests"}
                {pagePath === "/dashboard/categories" &&
                  "Manage service categories"}
                {pagePath === "/dashboard/category-requests" &&
                  "Review provider requests for categories and subcategories not yet listed"}
                {pagePath === "/dashboard/support" &&
                  "View and respond to customer and provider support cases"}
                {pagePath === "/dashboard/settings" &&
                  "Configure Payment/Commission settings"}
                {pagePath === "/dashboard/marketing" &&
                  "Send targeted and personalised push and email campaigns"}
                {pagePath === "/dashboard/communication" &&
                  "Edit the live email formats used by the platform"}
                {(pagePath === "/dashboard/notifications" ||
                  pagePath === "/dashboard/sms") &&
                  "Edit the live push notification formats used by the platform"}
                {pagePath === "/dashboard/team-access" &&
                  "Create teams and assign role-based dashboard access"}
                {(pagePath === "/dashboard/policies" ||
                  pagePath === "/dashboard/privacy-policy" ||
                  pagePath === "/dashboard/terms-provider" ||
                  pagePath === "/dashboard/terms-customer") &&
                  "View or edit policies and terms"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {canUseIndia && !FORCED_COUNTRY && (
                <Select
                  size="small"
                  value={activeCountry}
                  onChange={(event) => setCountry(event.target.value)}
                  aria-label="Country"
                  sx={{ minWidth: 150, fontWeight: 600 }}
                >
                  {DASHBOARD_COUNTRIES.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <IconButton onClick={handleNotifOpen}>
                <Badge badgeContent={notificationsCount} color="error">
                  <NotificationsIcon sx={{ color: "#666" }} />
                </Badge>
              </IconButton>
              {/* Notifications dropdown */}
              <Menu
                anchorEl={notifAnchorEl}
                open={Boolean(notifAnchorEl)}
                onClose={handleNotifClose}
                PaperProps={{ sx: { mt: 1.5, minWidth: 360 } }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1">Notifications</Typography>
                  <Box>
                    <Button size="small" onClick={handleMarkAllRead}>
                      Mark all read
                    </Button>
                  </Box>
                </Box>
                {shownNotifications.length === 0 ? (
                  <MenuItem disabled sx={{ justifyContent: "center" }}>
                    No notifications
                  </MenuItem>
                ) : (
                  shownNotifications.slice(0, 50).map((n) => (
                    <MenuItem key={n.id} sx={{ alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flex: 1,
                        }}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <Avatar
                          src={n.imageURL || undefined}
                          sx={{ width: 40, height: 40 }}
                        >
                          {!n.imageURL && (n.message?.charAt(0) || "?")}
                        </Avatar>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: n.read ? 400 : 700 }}
                          >
                            {n.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {n.type} •{" "}
                            {n.createdAt && n.createdAt.toDate
                              ? n.createdAt.toDate().toLocaleString()
                              : n.createdAt
                              ? new Date(n.createdAt).toLocaleString()
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(n);
                          }}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(n.id);
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Menu>

              {/* <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {currentUser?.displayName?.split(' ').map(n => n[0]).join('') || 'AM'}
              </Typography> */}

              <IconButton onClick={handleMenu} sx={{ p: 0 }}>
                <Avatar
                  src={currentUser?.photoURL || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: "#4A5BF4",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  {!currentUser?.photoURL &&
                    (currentUser?.displayName?.charAt(0) ||
                      currentUser?.email?.charAt(0)?.toUpperCase() ||
                      "A")}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setProfileOpen(true);
                    handleClose();
                  }}
                >
                  <AccountCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                  Admin Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                  <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                  Logout
                </MenuItem>
              </Menu>
              <AdminProfileDialog
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
              />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden", p: 4 }}>
          {activeCountry === "IN" && !canUseIndia ? (
            <Typography color="text.secondary">
              Your account does not have access to any India pages.
            </Typography>
          ) : (
            <Outlet />
          )}
        </Box>

        <Snackbar
          open={supportCaseSnackbar.open}
          autoHideDuration={7000}
          onClose={closeSupportCaseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeSupportCaseSnackbar}
            severity="info"
            variant="filled"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={openSupportCaseFromSnackbar}
              >
                Open
              </Button>
            }
            sx={{ width: "100%" }}
          >
            {supportCaseSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Dashboard;
